import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { InputAdornment, MenuItem, Typography } from '@mui/material';
import { t } from 'i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { StyledInputLabel } from '../style';
import { StyledFormControl, StyledFormHelperText, StyledSelect } from './style';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
};

const Dropdown = ({
  options = [],
  defaultValue = '',
  name,
  label,
  values,
  handleChange,
  error,
  helperText,
  disabled,
  required = false,
  countryField = false,
  valueData = false,
  placeholder = t('form.select'),
  forCustomField = false
}) => {
  const {
    control,
    formState: { errors },
    clearErrors
  } = useFormContext();

  // Use provided error and helperText if available, otherwise fall back to formState
  const hasError = error ?? !!errors[name];
  const helperTextToShow = helperText || errors[name]?.message || '';

  const handleSelectChange = (event, field) => {
    const selectedValue = event.target.value;

    // Update the field value
    field.onChange(event);

    // Clear the error when a value is selected
    if (selectedValue) {
      clearErrors(name);
    }

    // Call any additional change handlers passed in
    if (handleChange) {
      handleChange(event);
    }
  };

  return (
    <>
      <StyledInputLabel htmlFor={name}>
        {label}
        {required && <span className="required">*</span>}
      </StyledInputLabel>
      <StyledFormControl fullWidth error={hasError}>
        <Controller
          name={name}
          control={control}
          defaultValue={defaultValue}
          render={({ field }) => (
            <>
              <StyledSelect
                id={`${name}-select`}
                {...field}
                value={values || field?.value || defaultValue}
                onChange={(event) => handleSelectChange(event, field)}
                displayEmpty
                MenuProps={MenuProps}
                IconComponent={ExpandMoreIcon}
                sx={{
                  '& .MuiSelect-icon': {
                    fontSize: '2rem'
                  }
                }}
                InputProps={{
                  endAdornment: hasError && (
                    <InputAdornment position="end">
                      <ErrorOutlineIcon sx={{ color: 'red' }} />
                    </InputAdornment>
                  )
                }}
                disabled={disabled}>
                <MenuItem value="" disabled={!countryField}>
                  <Typography
                    color={countryField ? 'inherit' : 'textSecondary'}
                    sx={{ fontSize: '14px' }}>
                    {placeholder}
                  </Typography>
                </MenuItem>
                {options?.map(({ id, label, value, name }) => {
                  const displayName = forCustomField ? name : t(`${name}`);
                  // Use `label` directly for display; fallback to `name` if `label` is undefined
                  const displayLabel = label || displayName;

                  return (
                    <MenuItem
                      key={id}
                      value={valueData ? value : label || displayName} // Ensure `value` does not interfere with `label`
                      sx={{ fontSize: '14px' }}>
                      {displayLabel}
                    </MenuItem>
                  );
                })}
              </StyledSelect>
              <StyledFormHelperText>{helperTextToShow}</StyledFormHelperText>
            </>
          )}
        />
      </StyledFormControl>
    </>
  );
};

export default Dropdown;
